import React, { useState } from "react";
import { US_STATES } from "../data/UsState";

import "./UsaMap.css";

// Wider than the default 975 so the label column on the right has room.
// (Your data file says "0 0 975 610"; the extra 40 units are only for labels.)
const MAP_VIEWBOX = "0 0 1015 610";

// ---------------------------------------------------------------------------
// Label positions, in the same coordinate system as the state paths.
// Each point is the "most inside" spot of the state.
// ---------------------------------------------------------------------------
const STATE_LABEL_POSITIONS = {
  AK: { x: 107, y: 510 },
  AL: { x: 674, y: 441 },
  AR: { x: 561, y: 389 },
  AZ: { x: 207, y: 394 },
  CA: { x: 86, y: 318 },
  CO: { x: 318, y: 289 },
  FL: { x: 791, y: 535 },
  GA: { x: 744, y: 439 },
  HI: { x: 317, y: 586 },
  IA: { x: 537, y: 228 },
  ID: { x: 192, y: 163 },
  IL: { x: 612, y: 267 },
  IN: { x: 668, y: 275 },
  KS: { x: 468, y: 309 },
  KY: { x: 703, y: 316 },
  LA: { x: 562, y: 457 },
  ME: { x: 921, y: 92 },
  MI: { x: 684, y: 200 },
  MN: { x: 521, y: 115 },
  MO: { x: 560, y: 317 },
  MS: { x: 617, y: 422 },
  MT: { x: 259, y: 97 },
  NC: { x: 818, y: 351 },
  ND: { x: 442, y: 104 },
  NE: { x: 431, y: 240 },
  NM: { x: 312, y: 391 },
  NV: { x: 139, y: 240 },
  NY: { x: 844, y: 166 },
  OH: { x: 720, y: 257 },
  OK: { x: 490, y: 379 },
  OR: { x: 114, y: 139 },
  PA: { x: 820, y: 223 },
  SC: { x: 787, y: 395 },
  SD: { x: 412, y: 171 },
  TN: { x: 673, y: 362 },
  TX: { x: 456, y: 480 },
  UT: { x: 227, y: 278 },
  VA: { x: 810, y: 300 },
  WA: { x: 132, y: 59 },
  WI: { x: 595, y: 161 },
  WV: { x: 759, y: 295 },
  WY: { x: 304, y: 195 },
};

// Small north-east states: label is drawn outside, joined to the state by a
// leader line that starts with a dot INSIDE the state (from) and ends at the
// label. side "left"  -> text ends at x (label sits left of the state)
//        side "right" -> text starts at x (labels in a column on the right)
const OUTSIDE_LABELS = {
  NH: { side: "left", x: 880, y: 92, from: [891, 108] },
  VT: { side: "left", x: 872, y: 106, from: [886, 114] },
  MA: { side: "right", x: 944, y: 176, from: [882, 173] },
  RI: { side: "right", x: 944, y: 192, from: [903, 181] },
  CT: { side: "right", x: 944, y: 208, from: [889, 189] },
  NJ: { side: "right", x: 944, y: 240, from: [863, 241] },
  DE: { side: "right", x: 944, y: 262, from: [856, 267] },
  MD: { side: "right", x: 944, y: 284, from: [857, 279] },
  DC: { side: "right", x: 944, y: 306, from: [828, 268] },
};

// Text shown instead of the full name
const LABEL_TEXT = { DC: "D.C." };

// Two-word names are split over two lines so they fit inside the state
const TWO_LINE = ["ND", "SD", "NM", "WV", "NC", "SC", "NY"];

// Font size in map units
const FONT_SIZE = { MS: 6, NC: 6.5, SC: 6.5, WV: 6.5 };
const DEFAULT_FONT_SIZE = 9;

const USAMap = ({ onStateSelect }) => {
  const [selectedState, setSelectedState] = useState(null);

  const handleStateClick = (state) => {
    setSelectedState(state.id);
    onStateSelect(state);
  };

  const renderLabel = (state) => {
    const text = (LABEL_TEXT[state.id] || state.name).toUpperCase();
    const isSelected = selectedState === state.id;
    const cls = `usa-state-label ${isSelected ? "selected" : ""}`;
    const fontSize = FONT_SIZE[state.id] || DEFAULT_FONT_SIZE;

    // 1) small states: outside label + elbow leader line + dot
    const out = OUTSIDE_LABELS[state.id];
    if (out) {
      const [fx, fy] = out.from;
      const ty = out.y - fontSize * 0.35; // line meets the middle of the text
      const isLeft = out.side === "left";
      const endX = isLeft ? out.x + 3 : out.x - 3;
      const bendX = isLeft ? endX : endX - 8;
      return (
        <g key={`label-${state.id}`}>
          <polyline
            className="usa-leader-line"
            points={`${fx},${fy} ${bendX},${ty} ${endX},${ty}`}
          />
          <circle className="usa-leader-dot" cx={fx} cy={fy} r="1.3" />
          <text
            x={out.x}
            y={out.y}
            className="usa-state-label usa-state-label-outside"
            style={{ fontSize, textAnchor: isLeft ? "end" : "start" }}
          >
            {text}
          </text>
        </g>
      );
    }

    // 2) normal states: label inside the state
    const pos = STATE_LABEL_POSITIONS[state.id] ?? { x: state.cx, y: state.cy };

    if (TWO_LINE.includes(state.id) && text.includes(" ")) {
      const [first, second] = text.split(" ");
      return (
        <text
          key={`label-${state.id}`}
          className={cls}
          style={{ fontSize, textAnchor: "middle" }}
        >
          <tspan x={pos.x} y={pos.y - 1}>
            {first}
          </tspan>
          <tspan x={pos.x} y={pos.y + fontSize}>
            {second}
          </tspan>
        </text>
      );
    }

    return (
      <text
        key={`label-${state.id}`}
        x={pos.x}
        y={pos.y}
        className={cls}
        style={{ fontSize, textAnchor: "middle" }}
        dominantBaseline="middle"
      >
        {text}
      </text>
    );
  };

  return (
    <div className="usa-map-wrapper">
      <svg
        className="usa-map-svg"
        viewBox={MAP_VIEWBOX}
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* shapes first, then labels on top so no label is ever covered */}
        {US_STATES.map((state) => (
          <path
            key={state.id}
            d={state.d}
            className={`usa-state ${
              selectedState === state.id ? "selected" : ""
            }`}
            onClick={() => handleStateClick(state)}
          />
        ))}

        {US_STATES.map(renderLabel)}
      </svg>
    </div>
  );
};

export default USAMap;
